import { AppModal } from 'shared/UI/UIKit/Modal';
import { AppButton } from 'shared/UI/UIKit/Button';
import { AppFlex } from 'shared/UI/UIKit/Flex';
import { AppCheckbox } from 'shared/UI/UIKit/Checkbox';
import {
  educationParallelTextRepresentation,
  SCHOOL_PARALLEL_GROUPS,
} from 'entities/education-class';
import type { EducationClass, Parallel } from 'services/api';

type AddClassesModalProps = {
  visible: boolean;
  onCancel: () => void;
  educationClasses: EducationClass[];
  selectedClassKeys: string[];
  onChange: (keys: string[]) => void;
};

export const AddClassesModal = ({
  visible,
  onCancel,
  educationClasses,
  selectedClassKeys,
  onChange,
}: AddClassesModalProps) => {
  const getKey = (c: EducationClass) => `${c.parallel}-${c.litera}`;

  const allKeys = educationClasses.map(getKey);
  const allChecked = allKeys.length > 0 && allKeys.every((key) => selectedClassKeys.includes(key));
  const allIndeterminate =
    selectedClassKeys.length > 0 && !allChecked && selectedClassKeys.length < allKeys.length;

  const getGroup = (parallels: readonly Parallel[]) =>
    educationClasses.filter((c) => parallels.includes(c.parallel));

  const [primaryGroup, middleGroup, highGroup] = SCHOOL_PARALLEL_GROUPS;
  const primaryClasses = getGroup(primaryGroup.parallels);
  const middleClasses = getGroup(middleGroup.parallels);
  const highClasses = getGroup(highGroup.parallels);

  const primaryKeys = primaryClasses.map(getKey);
  const middleKeys = middleClasses.map(getKey);
  const highKeys = highClasses.map(getKey);

  const isGroupChecked = (keys: string[]) =>
    keys.length > 0 && keys.every((key) => selectedClassKeys.includes(key));

  const isGroupIndeterminate = (keys: string[]) => {
    const someSelected = keys.some((key) => selectedClassKeys.includes(key));
    return someSelected && !isGroupChecked(keys);
  };

  const handleCheckAll = (checked: boolean) => {
    onChange(checked ? allKeys : []);
  };

  const handleGroupChange = (keys: string[], checked: boolean) => {
    if (!keys.length) return;
    if (checked) {
      const merged = Array.from(new Set([...selectedClassKeys, ...keys]));
      onChange(merged);
    } else {
      const filtered = selectedClassKeys.filter((key) => !keys.includes(key));
      onChange(filtered);
    }
  };

  const handleClassChange = (key: string, checked: boolean) => {
    if (checked) {
      if (!selectedClassKeys.includes(key)) {
        onChange([...selectedClassKeys, key]);
      }
    } else {
      onChange(selectedClassKeys.filter((k) => k !== key));
    }
  };

  const footer = (
    <AppFlex direction="row">
      <AppButton type="outline" onClick={onCancel}>
        Закрыть
      </AppButton>
    </AppFlex>
  );

  const content = (
    <AppFlex direction="column" unit="12">
      <div>
        <AppCheckbox
          checked={allChecked}
          indeterminate={allIndeterminate}
          onChange={(e) => handleCheckAll(e.target.checked)}
        >
          Выбрать все классы
        </AppCheckbox>
      </div>

      <AppFlex direction="column" unit="8">
        {/* Начальная школа */}
        <AppCheckbox
          checked={isGroupChecked(primaryKeys)}
          indeterminate={isGroupIndeterminate(primaryKeys)}
          onChange={(e) => handleGroupChange(primaryKeys, e.target.checked)}
        >
          {primaryGroup.label}
        </AppCheckbox>
        <AppFlex direction="column" unit="4" style={{ marginLeft: 24 }}>
          {primaryClasses.map((cls) => {
            const label = `${educationParallelTextRepresentation[cls.parallel]}${cls.litera} класс`;
            const key = getKey(cls);
            return (
              <AppCheckbox
                key={cls.id}
                checked={selectedClassKeys.includes(key)}
                onChange={(e) => handleClassChange(key, e.target.checked)}
              >
                {label}
              </AppCheckbox>
            );
          })}
        </AppFlex>

        {/* Средняя школа */}
        <AppCheckbox
          checked={isGroupChecked(middleKeys)}
          indeterminate={isGroupIndeterminate(middleKeys)}
          onChange={(e) => handleGroupChange(middleKeys, e.target.checked)}
        >
          {middleGroup.label}
        </AppCheckbox>
        <AppFlex direction="column" unit="4" style={{ marginLeft: 24 }}>
          {middleClasses.map((cls) => {
            const label = `${educationParallelTextRepresentation[cls.parallel]}${cls.litera} класс`;
            const key = getKey(cls);
            return (
              <AppCheckbox
                key={cls.id}
                checked={selectedClassKeys.includes(key)}
                onChange={(e) => handleClassChange(key, e.target.checked)}
              >
                {label}
              </AppCheckbox>
            );
          })}
        </AppFlex>

        {/* Старшая школа */}
        <AppCheckbox
          checked={isGroupChecked(highKeys)}
          indeterminate={isGroupIndeterminate(highKeys)}
          onChange={(e) => handleGroupChange(highKeys, e.target.checked)}
        >
          {highGroup.label}
        </AppCheckbox>
        <AppFlex direction="column" unit="4" style={{ marginLeft: 24 }}>
          {highClasses.map((cls) => {
            const label = `${educationParallelTextRepresentation[cls.parallel]}${cls.litera} класс`;
            const key = getKey(cls);
            return (
              <AppCheckbox
                key={cls.id}
                checked={selectedClassKeys.includes(key)}
                onChange={(e) => handleClassChange(key, e.target.checked)}
              >
                {label}
              </AppCheckbox>
            );
          })}
        </AppFlex>
      </AppFlex>
    </AppFlex>
  );

  return (
    <AppModal
      visible={visible}
      onCancel={onCancel}
      title="Выбор классов"
      content={content}
      footer={footer}
      destroyOnClose
      maskClosable={false}
      size="auto"
      wrapStyle={{ width: 'auto', alignItems: 'center' }}
      style={{ width: 'auto' }}
    />
  );
};
